/**
 * Get it built — one order, for both sides (mockup docs/design/marketplace-hire/Workroom.dc.html).
 * Route /marketplace/build/orders/:id, `?tab=overview|stages|reports|payments|messages`.
 *
 * The client pays Twasta in the standard stages; the builder builds in their
 * own account; the client approves each stage and Twasta then pays the
 * builder; at sign-off the app is installed in the client's organization.
 * Every button comes from `order.can`; every action's response (the updated
 * order) replaces the screen's copy. Doc: docs/marketplace_get_it_built.md.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useAuth } from '@/auth/AuthContext'
import { fmtDate, mktError, money } from '@/api/marketplace'
import { ORDER_STATUS_LABELS, acceptOrder, declineOrder, getOrder, type Order, type OrderStatus } from '@/api/marketplaceBuild'
import MarketplaceShell from '../MarketplaceShell'
import PaymentStages from './PaymentStages'
import WalletTopUp from './WalletTopUp'
import {
  MoneyBar, NoteField, StagePill, TABS, currentStage, doneCount, firstName, fmtDateTime, hasMonthly, liveBy,
  monthlyText, type OrderCtx, type TabId,
} from './orderShared'
import OrderStagesTab from './orderStages'
import OrderReportsTab from './orderReports'
import OrderPaymentsTab from './orderPayments'
import OrderMessagesTab, { ActivityList, Bubble } from './orderMessages'

const STATUS_TONE: Record<OrderStatus, string> = { placed: 'info', in_progress: 'info', completed: 'ok', declined: 'bad', cancelled: '' }
const POLL_MS = 60_000

interface Due { short: number; retry: () => Promise<Order>; ok: string }

export default function OrderPage() {
  const { id } = useParams<{ id: string }>()
  const orderId = Number(id)
  const [params, setParams] = useSearchParams()
  const tabParam = params.get('tab') as TabId | null
  const tab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : 'overview'
  const { user } = useAuth()

  const [order, setOrder] = useState<Order | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [due, setDue] = useState<Due | null>(null)
  const busyRef = useRef(false)

  const reload = useCallback(async () => {
    if (!Number.isFinite(orderId) || orderId <= 0) { setError("We couldn't find that order."); return }
    try {
      const o = await getOrder(orderId)
      setOrder((prev) => (prev && prev.result && !o.result ? { ...o, result: prev.result } : o))
      setError(null)
    } catch (e) {
      setError(mktError(e, "We couldn't load this order.").message)
    }
  }, [orderId])
  useEffect(() => { setOrder(null); reload() }, [reload])

  // Light refresh while the page is open (the other side may have acted).
  useEffect(() => {
    const t = window.setInterval(() => {
      if (!busyRef.current && document.visibilityState === 'visible') reload()
    }, POLL_MS)
    return () => window.clearInterval(t)
  }, [reload])

  const act = useCallback(async (fn: () => Promise<Order>, ok: string): Promise<Order | null> => {
    setBusy(true)
    busyRef.current = true
    try {
      const o = await fn()
      setOrder(o)
      setDue(null)
      if (o.result?.install_error) toast.error(o.result.install_error.message || 'Signed off, but the install didn’t start.')
      else toast.success(ok)
      return o
    } catch (e) {
      const err = mktError(e)
      if (err.code === 'BUILD_PAYMENT_DUE') {
        setDue({ short: Number(err.extra.short_minor || err.extra.due_minor || 0), retry: fn, ok })
      }
      toast.error(err.message)
      return null
    } finally {
      setBusy(false)
      busyRef.current = false
    }
  }, [])

  const goTab = useCallback((t: TabId) => {
    const next = new URLSearchParams(params)
    if (t === 'overview') next.delete('tab'); else next.set('tab', t)
    setParams(next, { replace: true })
  }, [params, setParams])

  if (error && !order) {
    return (
      <MarketplaceShell>
        <div className="mkt-wrap" style={{ padding: '32px 20px' }}>
          <div className="mkt-banner bad" role="alert"><div><strong>Order not available</strong>{error}</div>
            <button type="button" className="btn btn-secondary" onClick={reload}>Try again</button>
          </div>
          <p><Link to="/marketplace/my-apps">Back to My apps</Link></p>
        </div>
      </MarketplaceShell>
    )
  }
  if (!order) {
    return <MarketplaceShell><div className="mkt-wrap" style={{ padding: 40 }} role="status">Loading the order…</div></MarketplaceShell>
  }

  const ctx: OrderCtx = { order, busy, act, setOrder, reload, goTab, me: user?.user_id }
  const cur = currentStage(order)
  const live = liveBy(order)
  const open = order.status === 'placed' || order.status === 'in_progress'

  return (
    <MarketplaceShell>
      <div className="mkt-wrap mkt-col" style={{ padding: '24px 20px 48px', gap: 20 }}>
        <Link to={order.role === 'builder' ? '/marketplace/builder' : '/marketplace/my-apps'} className="mkt-muted" style={{ fontSize: 14 }}>
          ← {order.role === 'builder' ? 'Your listings & work' : 'My apps'}
        </Link>

        <header className="mkt-panel mkt-col" style={{ gap: 16 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className="mkt-col" style={{ gap: 4, flex: '1 1 420px' }}>
              <div className="mkt-row" style={{ gap: 10 }}>
                <h1 style={{ fontSize: 26 }}>{order.title}</h1>
                <span className={`mkt-pill ${STATUS_TONE[order.status]}`}>{ORDER_STATUS_LABELS[order.status]}</span>
              </div>
              <span className="mkt-muted">
                Order {order.order_no}
                {order.role === 'builder'
                  ? ` · for ${order.client.name}${order.client.person ? ` (${order.client.person})` : ''}`
                  : ` · with ${order.builder.person || order.builder.name}${order.builder.person && order.builder.name ? ` (${order.builder.name})` : ''}`}
                {' · '}fixed price {money(order.price_minor, order.currency)}
                {live && open ? ` · live by ${fmtDate(live)}` : ''}
              </span>
              {cur && order.status === 'in_progress' && (
                <span className="mkt-row" style={{ gap: 8, fontSize: 14 }}>
                  <span className="mkt-muted">Now:</span><strong>{cur.name}</strong><StagePill stage={cur} role={order.role} />
                </span>
              )}
            </div>
            <div className="mkt-row">
              {order.preview_path && <a href={order.preview_path} target="_blank" rel="noopener" className="btn btn-primary">Try the working copy</a>}
              {order.project_path && <Link to={order.project_path} className="btn btn-primary">Open in the Workbench</Link>}
              {order.role === 'client' && order.status === 'completed' && <Link to="/marketplace/my-apps" className="btn btn-secondary">My apps</Link>}
            </div>
          </div>
          {order.status !== 'declined' && <MoneyBar order={order} />}
          <PaymentStages stages={order.stages} rows={order.stages} currency={order.currency}
            who={order.role === 'admin' ? 'admin' : order.role} done={doneCount(order)} review={cur?.status === 'in_review'}
            advanceRelease={order.terms.advance_release} compact />
        </header>

        {due && order.role === 'client' && (
          <div className="mkt-banner warn" role="alert">
            <div>
              <strong>Your organization wallet doesn’t have enough</strong>
              Add the difference and we’ll try again straight away.
            </div>
            <WalletTopUp currency={order.currency} suggestMinor={due.short} onPaid={() => { const d = due; act(d.retry, d.ok) }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDue(null)}>Not now</button>
          </div>
        )}

        <EndedBanner order={order} />
        {order.status === 'placed' && <PlacedPanel ctx={ctx} />}

        <div className="mkt-tabs" role="tablist" aria-label="Order">
          {TABS.map((t) => (
            <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={tab === t.id} aria-controls={`panel-${t.id}`}
              className={`mkt-tab${tab === t.id ? ' on' : ''}`} onClick={() => goTab(t.id)}>
              {t.label}{t.id === 'reports' && order.reports.length ? ` (${order.reports.length})` : ''}
            </button>
          ))}
        </div>

        <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`}>
          {tab === 'overview' && <Overview ctx={ctx} />}
          {tab === 'stages' && <OrderStagesTab {...ctx} />}
          {tab === 'reports' && <OrderReportsTab {...ctx} />}
          {tab === 'payments' && <OrderPaymentsTab {...ctx} />}
          {tab === 'messages' && <OrderMessagesTab {...ctx} />}
        </div>
      </div>
    </MarketplaceShell>
  )
}

function EndedBanner({ order }: { order: Order }) {
  if (order.status !== 'declined' && order.status !== 'cancelled') return null
  return (
    <div className="mkt-banner plain" role="status">
      <div>
        <strong>{ORDER_STATUS_LABELS[order.status]}{order.ended_at ? ` · ${fmtDate(order.ended_at)}` : ''}</strong>
        {order.end_reason ? `${order.end_reason}. ` : ''}
        {order.money.refunded_minor > 0 && order.role === 'client'
          ? `${money(order.money.refunded_minor, order.currency)} went back to your organization wallet.` : ''}
      </div>
    </div>
  )
}

/** Order placed: the builder accepts or declines; the client waits. */
function PlacedPanel({ ctx }: { ctx: OrderCtx }) {
  const { order, act, busy } = ctx
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')
  if (order.role !== 'builder') {
    return (
      <div className="mkt-banner info" role="status">
        <div>
          <strong>Waiting for {firstName(order.builder.person, order.builder.name)} to accept{order.accept_by ? ` — until ${fmtDateTime(order.accept_by)}` : ''}</strong>
          The advance is held by Twasta. If the order isn’t accepted in time, it is refunded to your organization wallet with the service fee.
        </div>
      </div>
    )
  }
  return (
    <section className="mkt-panel mkt-panel-accent mkt-col" aria-label="Accept the order">
      <h2>{order.client.name} placed this order</h2>
      <span className="mkt-muted">
        Accept it{order.accept_by ? ` by ${fmtDateTime(order.accept_by)}` : ''} to start. A project is created in your Twasta account with the brief attached,
        and {order.client.name} can try the working copy while you build.
        {order.terms.advance_release === 'builder_accepts' ? ' The advance is paid to you when you accept.' : ' The advance is paid to you with the first approval.'}
      </span>
      {order.can.accept && !declining && (
        <div className="mkt-row">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => act(() => acceptOrder(order.id), 'Order accepted — your project is ready')}>Accept the order</button>
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => setDeclining(true)}>Decline</button>
        </div>
      )}
      {declining && (
        <div className="mkt-col" style={{ gap: 10 }}>
          <NoteField id="decline-reason" label={`Tell ${order.client.name} why (optional)`} value={reason} onChange={setReason}
            hint="The client gets everything back, including the service fee." />
          <div className="mkt-row">
            <button type="button" className="btn btn-danger" disabled={busy}
              onClick={async () => { if (await act(() => declineOrder(order.id, reason.trim()), 'Order declined')) setDeclining(false) }}>Decline the order</button>
            <button type="button" className="btn btn-ghost" onClick={() => setDeclining(false)}>Not now</button>
          </div>
        </div>
      )}
    </section>
  )
}

function Overview({ ctx }: { ctx: OrderCtx }) {
  const { order, goTab, me } = ctx
  const client = order.role === 'client'
  const cur = currentStage(order)
  const builderFirst = firstName(order.builder.person, order.builder.name)
  const latest = order.reports[0]
  const messages = order.events.filter((e) => e.kind === 'message').slice(-3)
  const monthly = hasMonthly(order.monthly) ? monthlyText(order.monthly, order.currency) : ''

  const next = (() => {
    if (order.status !== 'in_progress' || !cur) return null
    if (client) {
      if (cur.status === 'in_review') return { tone: 'warn', title: `“${cur.name}” is ready for your approval`, text: `Try it in the working copy, then approve or ask for changes.${cur.review_due_at ? ` Review by ${fmtDate(cur.review_due_at)}.` : ''}`, cta: 'Review the stage', tab: 'stages' as TabId }
      if (cur.status === 'payment_due') return { tone: 'bad', title: `Payment needed for “${cur.name}”`, text: `${money(cur.client.total_minor, order.currency)} — your review starts once it is paid.`, cta: 'Pay now', tab: 'stages' as TabId }
      if (cur.status === 'disputed') return { tone: 'bad', title: `“${cur.name}” is disputed`, text: 'Its money is frozen until Twasta decides.', cta: 'See the stage', tab: 'stages' as TabId }
      return { tone: 'info', title: `${builderFirst} is working on “${cur.name}”`, text: cur.target_date ? `Expected around ${fmtDate(cur.target_date)}.` : '', cta: 'See all stages', tab: 'stages' as TabId }
    }
    if (order.can.submit) return { tone: 'info', title: `Working on “${cur.name}”`, text: cur.status === 'changes_requested' ? `${order.client.name} asked for changes.` : cur.target_date ? `Target date ${fmtDate(cur.target_date)}.` : '', cta: 'Submit for approval', tab: 'stages' as TabId }
    if (cur.status === 'in_review') return { tone: 'warn', title: `“${cur.name}” is with ${order.client.name} for approval`, text: cur.review_due_at ? `Review due by ${fmtDate(cur.review_due_at)}.` : '', cta: 'See the stage', tab: 'stages' as TabId }
    return { tone: 'info', title: `Now: “${cur.name}”`, text: '', cta: 'See the stage', tab: 'stages' as TabId }
  })()

  return (
    <div className="mkt-split">
      <div className="mkt-main">
        {next && (
          <div className={`mkt-banner ${next.tone}`} role="status">
            <div><strong>{next.title}</strong>{next.text}</div>
            <button type="button" className="btn btn-primary" onClick={() => goTab(next.tab)}>{next.cta}</button>
          </div>
        )}
        {order.status === 'completed' && client && (
          <div className="mkt-banner ok" role="status">
            <div><strong>Installed in {order.client.name}</strong>Your own copy and database. Open it from My apps.</div>
            <Link to="/marketplace/my-apps" className="btn btn-primary">Open it in My apps</Link>
          </div>
        )}

        <div className="mkt-banner plain">
          <div>
            <strong>Where it lives</strong>
            {client
              ? `Built in ${builderFirst}’s Twasta account (${order.builder.name}). At sign-off it is installed in ${order.client.name} — your own copy and database — and appears in My apps${monthly ? ` at ${monthly}` : ''}.`
              : `You build it in your own Twasta account. At sign-off Twasta installs a copy in ${order.client.name}’s organization — their own copy and database${monthly ? `, at ${monthly}` : ''}. Their data never lives in your account.`}
          </div>
        </div>

        {latest && (
          <section className="mkt-panel mkt-col" aria-label="Latest progress report">
            <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
              <h2>Latest progress report</h2>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => goTab('reports')}>All reports</button>
            </div>
            <span className="mkt-faint">{latest.period_label} · sent {fmtDate(latest.sent_at)}</span>
            <strong>{latest.summary || `${latest.done.length} done · ${latest.next.length} next`}</strong>
            {latest.needs.some((n) => !n.done) && (
              <span className="mkt-msg warn">{latest.needs.filter((n) => !n.done).length} thing(s) needed from {client ? 'you' : order.client.name}</span>
            )}
          </section>
        )}

        <section className="mkt-panel mkt-col" aria-label="What happened recently">
          <h2>What happened recently</h2>
          <ActivityList events={order.events} limit={8} />
        </section>
      </div>

      <aside className="mkt-aside">
        <section className="mkt-panel mkt-col" aria-label="Messages" style={{ gap: 10 }}>
          <div className="mkt-row" style={{ justifyContent: 'space-between' }}>
            <h2>Messages</h2>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => goTab('messages')}>Open</button>
          </div>
          {messages.length ? messages.map((m) => <Bubble key={m.id} ev={m} mine={!!me && m.actor_user_id === me} />)
            : <span className="mkt-muted" style={{ fontSize: 14 }}>No messages yet.</span>}
        </section>
        <section className="mkt-panel mkt-col" aria-label="People on this order" style={{ gap: 10 }}>
          <h2>People on this order</h2>
          <Person initials={initialsOf(order.client.person || order.client.name)} name={order.client.person || order.client.name}
            role={`Client · ${order.client.name}`} />
          <Person initials={order.builder.initials || initialsOf(order.builder.person)} name={order.builder.person || order.builder.name}
            role={`Builder · the app lives in ${order.builder.name}’s account`} />
          {order.builder.tenant_id && <Link to={`/marketplace/builders/${encodeURIComponent(order.builder.tenant_id)}`} style={{ fontSize: 14 }}>See {builderFirst}’s profile</Link>}
        </section>
        <section className="mkt-panel mkt-col" aria-label="Payment progress" style={{ gap: 8 }}>
          <h2>Payments</h2>
          <span className="mkt-muted" style={{ fontSize: 14 }}>
            {doneCount(order)} of {order.stages.length} stages paid to {client ? builderFirst : 'you'}.
          </span>
          <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => goTab('payments')}>See payments</button>
        </section>
      </aside>
    </div>
  )
}

function initialsOf(name: string | null | undefined): string {
  const w = (name || '').split(/[\s@._-]+/).filter(Boolean)
  return ((w[0]?.[0] || '?') + (w[1]?.[0] || '')).toUpperCase()
}

function Person({ initials, name, role }: { initials: string; name: string; role: string }) {
  return (
    <div className="mkt-row" style={{ gap: 10, flexWrap: 'nowrap' }}>
      <span aria-hidden="true" className="mkt-tile" style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--color-accent)', fontSize: 13 }}>{initials}</span>
      <span className="mkt-col" style={{ gap: 0 }}><strong style={{ fontSize: 14 }}>{name}</strong><span className="mkt-faint">{role}</span></span>
    </div>
  )
}
